import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import {
  useCreateCredential,
  useCredentials,
  useRotateCredential,
  useSites,
  useUpdateCredential,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import { metricsApi } from '@/services/metrics';
import { useMutation } from '@tanstack/react-query';
import type { Credential, WpSite } from '@/types/config';

import styles from './ConfigAnalytics.module.css';

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

interface EditState {
  site: WpSite;
  ga4?: Credential;
  gsc?: Credential;
}

/**
 * Config → Desempenho (Analytics). Uma credencial GA4 + uma GSC POR SITE
 * (convenção do backend: provider ga4|gsc, slug = slug do site). O mesmo
 * service account JSON serve aos dois — cola-se uma vez e grava nos dois.
 * Liga o motor de coleta dormente (B-11): assim que houver credencial válida,
 * o sync diário (e o botão "Sincronizar agora") puxa GA4 + Search Console.
 */
export function ConfigAnalytics() {
  const { data: sites = [] } = useSites(true);
  const { data: ga4Creds = [] } = useCredentials('ga4');
  const { data: gscCreds = [] } = useCredentials('gsc');
  const createCred = useCreateCredential();
  const updateCred = useUpdateCredential();
  const rotateCred = useRotateCredential();

  const ga4BySlug = useMemo(
    () => Object.fromEntries(ga4Creds.map((c: Credential) => [c.slug, c])),
    [ga4Creds],
  );
  const gscBySlug = useMemo(
    () => Object.fromEntries(gscCreds.map((c: Credential) => [c.slug, c])),
    [gscCreds],
  );

  const [editing, setEditing] = useState<EditState | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [json, setJson] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const sync = useMutation({
    mutationFn: () => metricsApi.sync(),
    onSuccess: (r) => setMsg(`Sync concluído — ${r.updated} matéria(s) atualizada(s).`),
    onError: (e) => setMsg(`Erro no sync: ${describeError(e)}`),
  });

  function openEdit(site: WpSite) {
    const ga4 = ga4BySlug[site.slug] as Credential | undefined;
    const gsc = gscBySlug[site.slug] as Credential | undefined;
    setEditing({ site, ga4, gsc });
    setPropertyId(ga4?.config?.property_id != null ? String(ga4.config.property_id) : '');
    setSiteUrl(gsc?.config?.site_url != null ? String(gsc.config.site_url) : '');
    setJson('');
    setMsg(null);
  }

  function closeEdit() {
    setEditing(null);
    setPropertyId('');
    setSiteUrl('');
    setJson('');
  }

  async function saveProvider(
    provider: 'ga4' | 'gsc',
    slug: string,
    config: Record<string, unknown>,
    secret: string,
    existing?: Credential,
  ) {
    if (existing) {
      await updateCred.mutateAsync({
        id: existing.id,
        payload: { config: { ...existing.config, ...config }, active: true },
      });
      if (secret) {
        await rotateCred.mutateAsync({
          id: existing.id,
          payload: { secret },
        });
      }
    } else {
      await createCred.mutateAsync({
        provider,
        slug,
        secret: secret || undefined,
        config,
        active: true,
        notes: 'analytics (config → desempenho)',
      });
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);

    const slug = editing.site.slug;
    const pid = propertyId.trim();
    const url = siteUrl.trim();
    const sa = json.trim();

    if (!pid && !url) {
      setMsg('Informe o Property ID (GA4) e/ou a URL da propriedade (GSC).');
      return;
    }
    // JSON é obrigatório na 1ª configuração de cada provedor que vai ser usado.
    const ga4NeedsSecret = Boolean(pid) && !editing.ga4?.has_secret;
    const gscNeedsSecret = Boolean(url) && !editing.gsc?.has_secret;
    if ((ga4NeedsSecret || gscNeedsSecret) && !sa) {
      setMsg('Cole o Service Account (JSON) — necessário na 1ª configuração.');
      return;
    }
    if (sa) {
      try {
        JSON.parse(sa);
      } catch {
        setMsg('O Service Account não é um JSON válido. Cole o conteúdo do arquivo da chave.');
        return;
      }
    }

    try {
      if (pid) await saveProvider('ga4', slug, { property_id: pid }, sa, editing.ga4);
      if (url) await saveProvider('gsc', slug, { site_url: url }, sa, editing.gsc);
      setMsg(
        `Credenciais de ${editing.site.name} guardadas. Use "Sincronizar agora" para coletar.`,
      );
      closeEdit();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  function statusBadge(cred: Credential | undefined, hasConfig: boolean) {
    if (cred?.has_secret && hasConfig)
      return (
        <Badge tone="ok" size="sm">
          configurado
        </Badge>
      );
    if (cred || hasConfig)
      return (
        <Badge tone="warn" size="sm">
          incompleto
        </Badge>
      );
    return (
      <Badge tone="warn" size="sm">
        pendente
      </Badge>
    );
  }

  const saving = createCred.isPending || updateCred.isPending || rotateCred.isPending;

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>Desempenho (Analytics)</h1>
        <p className={styles.subtitle}>
          Conecte <b>Google Analytics 4</b> e <b>Search Console</b> por site. O painel “Desempenho”
          no editor passa a mostrar tráfego, engajamento e busca de cada matéria. Um{' '}
          <code>service account</code> (JSON) com acesso de leitura serve aos dois — cole-o uma vez
          por site. As chaves ficam Fernet-encrypted.
        </p>
      </header>

      <div className={styles.syncBar}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => sync.mutate()}
          loading={sync.isPending}
        >
          Sincronizar agora
        </Button>
        <span className={styles.syncHint}>
          puxa GA4 + Search Console de todos os sites configurados (também roda no sync diário).
        </span>
      </div>

      {msg && <p className={styles.flash}>{msg}</p>}

      {sites.length === 0 && (
        <p className={styles.empty}>
          Nenhum site cadastrado. Cadastre um destino em <b>Config → Sites WordPress</b> primeiro.
        </p>
      )}

      <ul className={styles.list}>
        {sites.map((site: WpSite) => {
          const ga4 = ga4BySlug[site.slug] as Credential | undefined;
          const gsc = gscBySlug[site.slug] as Credential | undefined;
          const pid = ga4?.config?.property_id;
          const url = gsc?.config?.site_url;
          return (
            <li key={site.id} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{site.name}</span>
                <code className={styles.slug}>{site.slug}</code>
              </div>

              <div className={styles.provRow}>
                <span className={styles.provLabel}>GA4</span>
                {statusBadge(ga4, Boolean(pid))}
                {pid != null && <span className={styles.mono}>property {String(pid)}</span>}
              </div>
              <div className={styles.provRow}>
                <span className={styles.provLabel}>Search Console</span>
                {statusBadge(gsc, Boolean(url))}
                {url != null && <span className={styles.mono}>{String(url)}</span>}
              </div>

              <div className={styles.actions}>
                <Button size="sm" variant="secondary" onClick={() => openEdit(site)}>
                  {ga4 || gsc ? 'Editar credenciais' : 'Conectar'}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {editing && (
        <div className={styles.modalBackdrop} onClick={closeEdit}>
          <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h2 className={styles.modalTitle}>Conectar — {editing.site.name}</h2>
            <p className={styles.modalSub}>
              site <code>{editing.site.slug}</code>. Property ID e URL são guardados em claro; o
              JSON do service account é encriptado. Deixe o JSON vazio para manter o atual.
            </p>

            <label className={styles.field}>
              <span>GA4 · Property ID</span>
              <input
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                placeholder="493871234"
                inputMode="numeric"
              />
              <span className={styles.hint}>
                GA4 → Admin → Configurações da propriedade (numérico).
              </span>
            </label>

            <label className={styles.field}>
              <span>Search Console · URL da propriedade</span>
              <input
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://www.seusite.com.br/"
              />
              <span className={styles.hint}>
                Propriedade verificada no Search Console (com a / final).
              </span>
            </label>

            <label className={styles.field}>
              <span>
                Service Account (JSON)
                {editing.ga4?.has_secret || editing.gsc?.has_secret
                  ? ' — vazio mantém o atual'
                  : ''}
              </span>
              <textarea
                className={styles.textarea}
                value={json}
                onChange={(e) => setJson(e.target.value)}
                placeholder={
                  '{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key": "..."\n}'
                }
                rows={6}
                spellCheck={false}
              />
              <span className={styles.hint}>
                Conta com papel <b>Leitor</b> na propriedade GA4 e adicionada como usuário no Search
                Console. Cole o conteúdo do arquivo .json da chave.
              </span>
            </label>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={closeEdit}>
                Cancelar
              </Button>
              <Button type="submit" loading={saving}>
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
